import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { FundraiserService } from '../../../../services/fundraiser.service';
import { MediaService } from '../../../../services/media.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { forkJoin } from 'rxjs';
import { AccountService } from 'src/app/pages/account/services/account.service';
import { MatIconModule } from '@angular/material/icon';
import { FundraiserMediaEditComponent } from '../../../shared/fundraiser-media-edit/fundraiser-media-edit.component';
import { QuillEditorComponent } from '../../../../../../shared/components/quill-editor/quill-editor.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatButtonModule } from '@angular/material/button';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { NgClass, NgIf } from '@angular/common';
type LOCALE =
	| 'nl'
	| 'en'
	| 'es'
	| 'de'
	| 'fr'
	| 'bg'
	| 'hr'
	| 'cs'
	| 'da'
	| 'fi'
	| 'el'
	| 'hu'
	| 'it'
	| 'pl'
	| 'pt'
	| 'ro'
	| 'sk'
	| 'sv'
	| 'uk';

@Component({
    selector: 'app-edit-update',
    templateUrl: './edit-update.component.html',
    standalone: true,
    imports: [NgClass, ExtendedModule, MatButtonModule, FlexModule, NgIf, MatProgressSpinnerModule, MatDialogModule, MatFormFieldModule, QuillEditorComponent, FundraiserMediaEditComponent, MatIconModule]
})
export class EditUpdateComponent implements OnInit {
	newUpdate: any = {};
	isLoading: boolean = false;
	allTextCheck: boolean = false;
	originalLocale: LOCALE = 'nl';
	languageIconPath: string =
		'https://whydonate.com/cdn-cgi/imagedelivery/_0vgnXOEIHPwLg2E52a7gg/shared/';
	allLanguages = {
		bg: 'Bulgarian',
		hr: 'Croatian',
		cs: 'Czech',
		da: 'Danish',
		fi: 'Finnish',
		el: 'Greek',
		hu: 'Hungarian',
		it: 'Italian',
		pl: 'Polish',
		pt: 'Portuguese',
		ro: 'Romanian',
		sk: 'Slovak',
		sv: 'Swedish',
		uk: 'Ukrainian',
		nl: 'Dutch',
		de: 'Deutsch',
		es: 'Spanish',
		en: 'English',
		fr: 'French',
	};
	loadingText: boolean = false;
	constructor(
		private _fundraiserService: FundraiserService,
		public _mediaService: MediaService,
		private notificationService: NotificationService,
		private accountService: AccountService,
		@Inject(MAT_DIALOG_DATA)
		public data: {
			update: any;
			fundraiserLocalId: number;
			slug: string;
			currentFundraiser: any;
		},
		public dialogRef: MatDialogRef<EditUpdateComponent>
	) {}
	ngOnInit(): void {
		this.originalLocale = this.data?.update?.userLocale;
	}

	changeUpdateContent(event: any) {
		this.newUpdate['content'] = event;
	}

	changeUpdateMedia(event: any) {
		this.newUpdate['imageList'] = event;
	}

	allCheck(event: boolean) {
		this.allTextCheck = event;
	}

	async saveUpdateContentAndMedia() {
		if (this.accountService.checkHeaders()) {
			this.isLoading = true;
			let observableSourceList: any[] = [];

			// Handle media if image list exists
			if (this.newUpdate?.imageList !== undefined) {
				let mediaBody = this.getMediaBodyObject();
				observableSourceList.push(
					this._mediaService.saveUpdateMedia(mediaBody)
				);
			}

			// Process content if exists
			if (this.newUpdate?.content) {
				let contentBody = this.newUpdate.content;

				// Process the content to replace <ul> and its <li> children
				contentBody = contentBody
					.replace(
						/<ul>(.*?)<\/ul>/gs,
						(match: string, innerContent: string): string => {
							return innerContent.replace(
								/<li>(.*?)<\/li>/g,
								'&#8226;&nbsp;$1<br>' // Replace <li> inside <ul> with bullet points
							);
						}
					)
					.replace(
						/<ol>(.*?)<\/ol>/gs,
						(match: string, innerContent: string): string => {
							let index = 1; // Start numbering from 1
							return innerContent.replace(
								/<li>(.*?)<\/li>/g,
								(_, item: string): string => {
									return `${index++}.&nbsp;${item}<br>`; // Replace <li> inside <ol> with numbered list
								}
							);
						}
					);

				// Sanitize the content using DOMPurify
				// const DOMPurify = require('dompurify');
				// contentBody = DOMPurify.sanitize(contentBody);

				// Now update the contentBody object
				let contentBodyObject = this.getContentBodyObject();
				contentBodyObject.content = contentBody;

				if (contentBodyObject.id) {
					observableSourceList.push(
						this._fundraiserService.saveEditedUpdates(contentBodyObject)
					);
				} else {
					this.notificationService.openNotification(
						$localize`:@@loading_please_wait:Loading Please Wait`,
						'close',
						'error'
					);
				}
			}

			// Execute all the observables
			forkJoin(observableSourceList).subscribe(
				(results) => {
					this.isLoading = false;
					this.notificationService.openNotification(
						$localize`:@@fundraiser_update_edit_notification:Fundraiser Update is updated`,
						'',
						'success'
					);
					window.location.reload();
				},
				(err: any) => {
					this.notificationService.openNotification(
						$localize`:@@edit_update_errorUpdating_notification:There was an error updating Fundraiser Update`,
						'close',
						'error'
					);
					this.isLoading = false;
				}
			);
		}
	}

	getMediaBodyObject() {
		let mediaBody = new FormData();
		this.newUpdate?.imageList.forEach((file: any, index: number) => {
			if (file.image) {
				mediaBody.append(
					'image' + index,
					this.newUpdate?.imageList[index]?.image
				);
			}
			if (file.videoUrl) {
				mediaBody.append(
					'video_url' + index,
					this.newUpdate?.imageList[index]?.videoUrl
				);
			}
		});
		mediaBody.append('text', 'update_' + this.data?.update?.id?.toString());
		mediaBody.append('slug', this.data?.slug);
		return mediaBody;
	}

	getContentBodyObject() {
		return {
			content: this.newUpdate?.content,
			language: this.data?.update?.userLocale,
			id: this.data?.update?.id,
			slug: this.data?.slug,
		};
	}
	onCloseClick() {
		this.dialogRef.close();
	}
	handlePasteDetected(loader: boolean) {
		this.loadingText = loader;
	}
}
